import { Injectable } from '@angular/core';
import { CsmsNode } from 'api/charging_stations.api';
import { take } from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Ticket } from 'api/ticketing.api';
import { CeLocationRow } from 'shared/stations-service/locations.service';
import {
    SearchApiService,
    GlobalSearchConnectorResult,
    GlobalSearchLocationResult,
    GlobalSearchStationResult,
    GlobalSearchOrganizationResult,
} from '@kempoweroyj/ngx-search-api-client';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Evse } from '@kempoweroyj/ngx-evse-api-client';
import { AuthService } from 'providers/auth.service';

const MAX_RESULTS_DISPLAYED = 25;

export type SearchResult =
    | StationSearchResult
    | ConnectorSearchResult
    | TicketSearchResult
    | NoSearchResult
    | LocationSearchResult
    | OrganizationSearchResult;

export interface StationSearchResult {
    type: 'station';
    station?: CsmsNode;
    globalSearchStation?: GlobalSearchStationResult;
}
export interface ConnectorSearchResult {
    type: 'connector';
    station?: CsmsNode;
    connector?: Evse;
    globalSearchConnector?: GlobalSearchConnectorResult;
}
export interface TicketSearchResult {
    type: 'ticket';
    ticket?: Ticket;
}
export interface LocationSearchResult {
    type: 'location';
    location?: CeLocationRow;
    globalSearchLocation?: GlobalSearchLocationResult;
}
export interface OrganizationSearchResult {
    type: 'organization';
    globalSearchOrganization?: GlobalSearchOrganizationResult;
}

export interface NoSearchResult {
    type: 'no_results';
}
@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
export class SearchService {
    results$: Observable<SearchResult[]>;
    private text$ = new Subject<string>();
    private hasAccessTo$ = new Observable<{ locations: boolean; organizations: boolean }>();

    hasAccessTo = {
        locations: this.authService.hasAccessTo$(['fleet.group.read']),
        organizations: this.authService.profile$.pipe(
            switchMap(() => {
                const privileges = this.authService.getPermissions();
                return privileges.isSystemAdmin || privileges.isOrganizationAdmin ? of(true) : of(false);
            })
        ),
    };
    constructor(
        private searchApi: SearchApiService,
        private authService: AuthService
    ) {
        this.hasAccessTo$ = combineLatest([this.hasAccessTo.locations, this.hasAccessTo.organizations]).pipe(
            switchMap(([locations, organizations]) => {
                return of({ locations, organizations });
            })
        );

        this.results$ = combineLatest([
            this.text$.pipe(debounceTime(500), distinctUntilChanged()),
            this.hasAccessTo$,
        ]).pipe(
            // discard any ongoing search if there are new text
            switchMap(([text]) => {
                if (!text?.trim().length) {
                    return of(null);
                }
                return this.searchApi.globalSearch({ query: text.trim() });
            }),
            withLatestFrom(this.hasAccessTo$),
            map(([res, hasAccessTo]) => {
                if (!res) return [];
                const globalSearchResults: SearchResult[] = [];
                for (const item of res.results) {
                    if (item.type === 'location' && hasAccessTo?.locations) {
                        globalSearchResults.push({ type: item.type, globalSearchLocation: item });
                    } else if (item.type === 'station') {
                        globalSearchResults.push({ type: item.type, globalSearchStation: item });
                    } else if (item.type === 'connector') {
                        globalSearchResults.push({ type: item.type, globalSearchConnector: item });
                    } else if (item.type === 'organization' && hasAccessTo?.organizations) {
                        globalSearchResults.push({ type: item.type, globalSearchOrganization: item });
                    }
                }
                if (globalSearchResults.length === 0) {
                    globalSearchResults.push({ type: 'no_results' });
                }
                return take(globalSearchResults, MAX_RESULTS_DISPLAYED);
            })
        );
    }

    search(text: string) {
        this.text$.next(text);
    }
    clear() {
        this.text$.next(null);
    }
}
